import React, { useEffect, useRef, useState } from 'react';
import { Button } from 'tradera/components/atoms/button/button';
import useIsMobile from 'tradera/hooks/use-is-mobile';
import useIsLoggedIn from 'tradera/hooks/use-is-loggedin';
import { useTranslator, useUrlLocalizer } from 'tradera/lang/translate';
import cn from 'classnames';
import FallbackImage from './suggestion-fallback-sm.jpg?next-image';
import styles from './discover-suggestions.module.scss';
import { StartPageSection } from '../start-page-section/start-page-section';
import { StartPageSectionTitle } from '../start-page-section/start-page-section-title';
import { GtmService } from 'tradera/services/google-tagmanager-service';
import {
    useGetIdeasQuery,
    useGetTrendingSearchesQuery
} from 'tradera/state/services/discover-api';
import type {
    IdeaViewModel,
    TrendingSearchViewModel
} from 'tradera/state/services/types/webapi-discover-generated';

const mapTrendingSearchToIdeaModel = (
    trendingSearch: TrendingSearchViewModel
): IdeaViewModel => ({
    ...trendingSearch,
    name: trendingSearch.query || '',
    url: `/search?q=${trendingSearch.query}${
        trendingSearch.categoryId
            ? `&categoryId=${trendingSearch.categoryId}`
            : ''
    }&sortBy=MostBids`
});

const getFallbackImageUrl = (): string => {
    if (typeof FallbackImage === 'string') {
        return FallbackImage;
    }
    return FallbackImage.src || '';
};

export const DiscoverSuggestions = () => {
    const { toLocalizedUrl } = useUrlLocalizer();
    const { t } = useTranslator();
    const isLoggedIn = useIsLoggedIn();
    const isMobile = useIsMobile();
    const containerRef = useRef<HTMLDivElement | null>(null);

    const [amountOfElements, setAmountOfElements] = useState<number | null>(
        null
    );

    const { data: ideasData, isError: ideasError } = useGetIdeasQuery(
        undefined,
        {
            skip: !isLoggedIn
        }
    );

    const { data: trendingSearches, isError: searchesError } =
        useGetTrendingSearchesQuery(undefined, {
            skip: isLoggedIn
        });
    const searchesData = React.useMemo(
        () => trendingSearches?.map(mapTrendingSearchToIdeaModel),
        [trendingSearches]
    );
    const responseData = isLoggedIn ? ideasData?.ideas : searchesData;
    const isError = isLoggedIn ? ideasError : searchesError;

    useEffect(() => {
        const container = containerRef?.current;
        if (container && responseData) {
            if (!isMobile) {
                const children = Array.from(container.children);
                // Check which children are inside of the container on the y axis
                const childrenVisible = children.filter((child) => {
                    const childRect = child.getBoundingClientRect();
                    const containerRect = container.getBoundingClientRect();
                    return (
                        childRect.top >= containerRect.top &&
                        childRect.bottom <= containerRect.bottom
                    );
                });
                setAmountOfElements(childrenVisible.length);
            } else {
                setAmountOfElements(null);
            }
        }
    }, [responseData, isMobile]);

    if (isError || responseData?.length === 0) {
        return null;
    }

    return (
        <StartPageSection>
            <StartPageSectionTitle>
                {isLoggedIn
                    ? t('startPage_discoverSuggestions_title')
                    : t('startPage_discoverSuggestions_title_loggedOut')}
            </StartPageSectionTitle>
            <div className="overflow-x-auto pl-2 px-md-3 px-lg-2 px-xl-0">
                <div
                    className={cn(styles.suggestionsForYou, {
                        [styles.suggestionsSearches]: !isLoggedIn
                    })}
                    ref={containerRef}>
                    {responseData?.map((suggestion, index) => {
                        if (amountOfElements && index >= amountOfElements) {
                            return null;
                        }
                        const imageSrc = suggestion.imageUrlTemplate?.replace(
                            '{format}',
                            '250-square'
                        );
                        return (
                            <Button
                                key={suggestion.index}
                                theme="action"
                                variant="outline"
                                className={styles.suggestion}
                                href={toLocalizedUrl(suggestion.url)}
                                onClick={() =>
                                    GtmService.trackAction(
                                        'StartPage',
                                        'Discover suggestions',
                                        suggestion.name || ''
                                    )
                                }>
                                <img
                                    className={styles.suggestionImage}
                                    src={imageSrc || getFallbackImageUrl()}
                                    loading="lazy"
                                    alt=""
                                />
                                <span className="px-2 m-auto">
                                    {suggestion.name}
                                </span>
                            </Button>
                        );
                    })}
                </div>
            </div>
        </StartPageSection>
    );
};
